import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, authHeaders, errMessage } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Loader2, MapPin } from "lucide-react";
import { COUNTRIES, BIRTH_YEARS, GENDER_OPTIONS } from "@/lib/countries";
import InterestsPicker from "@/components/InterestsPicker";

// Shown after a Google sign-up, when the client has no demographic data yet.
// Google only returns email/name/picture, so we collect the rest here.
export default function CompleteClientProfile({ client, onDone }) {
  const [form, setForm] = useState({
    phone: client?.phone || "",
    city: client?.city || "",
    country: client?.country || "Italia",
    birth_date: client?.birth_date ? String(client.birth_date) : "",
    gender: client?.gender || "",
  });
  const [interests, setInterests] = useState(client?.interests || []);
  const [macros, setMacros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [pendingCity, setPendingCity] = useState("");

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non disponibile."); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.get(`/geo/reverse?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
          if (r.data?.city) setPendingCity(r.data.city);
          else toast.error("Non siamo riusciti a rilevare la città. Inseriscila a mano.");
        } catch (_) { toast.error("Non siamo riusciti a rilevare la città."); }
        finally { setGeoLoading(false); }
      },
      () => { setGeoLoading(false); toast.error("Permesso posizione negato."); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  useEffect(() => {
    api.get("/categories").then((r) => setMacros(r.data.categories || r.data || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.phone.trim()) return setError("Inserisci il numero di telefono.");
    if (!form.city.trim()) return setError("Inserisci la tua città.");
    if (!form.country) return setError("Seleziona la tua nazione.");
    if (!form.birth_date) return setError("Inserisci la tua data di nascita.");
    if (!form.gender) return setError("Seleziona il tuo genere.");
    setLoading(true);
    try {
      await api.put(
        "/client/auth/profile",
        { phone: form.phone, city: form.city, country: form.country, birth_date: form.birth_date, gender: form.gender, interests },
        { headers: authHeaders("client") }
      );
      toast.success("Profilo completato, grazie!");
      if (onDone) await onDone();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[3000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="complete-profile-overlay">
      <div className="w-full max-w-md max-h-[92dvh] overflow-y-auto rounded-3xl bg-white shadow-2xl">
        <div className="kn-gradient px-6 pt-7 pb-8 text-white">
          <div className="h-12 w-12 rounded-2xl bg-white/20 flex items-center justify-center mb-3"><Ticket className="h-6 w-6" /></div>
          <h2 className="text-2xl font-extrabold">Completa il tuo profilo</h2>
          <p className="text-white/85 text-sm mt-1">Ci servono ancora pochi dati per mostrarti le offerte giuste per te. Ci vuole un attimo.</p>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cp-phone">Telefono *</Label>
              <Input id="cp-phone" data-testid="cp-phone" value={form.phone} onChange={set("phone")} placeholder="+39 333..." />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-city">Città *</Label>
              <Input id="cp-city" data-testid="cp-city" value={form.city} onChange={set("city")} placeholder="Pesaro" />
            </div>
          </div>

          <button type="button" onClick={useMyLocation} disabled={geoLoading} data-testid="cp-use-location"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E63946] disabled:opacity-60">
            {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            Usa la mia posizione per la città
          </button>

          {pendingCity && (
            <div data-testid="cp-city-confirm" className="rounded-xl border border-[#E63946]/20 bg-[#FFF1F2] p-3 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-slate-700">Abbiamo rilevato <b>{pendingCity}</b>. È corretta?</span>
              <div className="flex gap-2 ml-auto">
                <button type="button" data-testid="cp-city-confirm-yes" onClick={() => { setForm((f) => ({ ...f, city: pendingCity })); setPendingCity(""); }}
                  className="px-3 py-1 rounded-full bg-[#E63946] text-white text-sm font-bold">Conferma</button>
                <button type="button" data-testid="cp-city-confirm-no" onClick={() => setPendingCity("")}
                  className="px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-semibold">No, la scrivo io</button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cp-country">Nazione *</Label>
              <select id="cp-country" data-testid="cp-country" value={form.country} onChange={set("country")}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]/30">
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-birthdate">Data di nascita *</Label>
              <input type="date" id="cp-birthdate" data-testid="cp-birthdate" value={form.birth_date} onChange={set("birth_date")}
                max={new Date().toISOString().slice(0, 10)} min="1900-01-01"
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]/30" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cp-gender">Genere *</Label>
            <select id="cp-gender" data-testid="cp-gender" value={form.gender} onChange={set("gender")}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]/30">
              <option value="">Seleziona…</option>
              {GENDER_OPTIONS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>

          {macros.length > 0 && (
            <div className="space-y-1.5">
              <Label>I tuoi interessi (per offerte su misura)</Label>
              <div data-testid="cp-interests">
                <InterestsPicker macros={macros} value={interests} onChange={setInterests} prefix="cp-interest" />
              </div>
            </div>
          )}

          {error && <p className="text-sm text-[#DC2626] font-medium" data-testid="cp-error">{error}</p>}

          <Button type="submit" disabled={loading} data-testid="cp-submit" className="w-full h-12 text-base font-bold rounded-xl">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva e continua"}
          </Button>
        </form>
      </div>
    </div>
  );
}
